import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { StatusProvider } from "@/context/StatusContext";
import { InboxProvider } from "@/context/InboxContext";
import { ReshipProvider } from "@/context/ReshipContext";
import { RvpProvider } from "@/context/RvpContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Analytics from "@/pages/Analytics";
import Shipments from "@/pages/Shipments";
import NewShipments from "@/pages/NewShipments";
import ReversePickup from "@/pages/ReversePickup";
import ShipmentDetail from "@/pages/ShipmentDetail";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import Profile from "@/pages/Profile";
import Inbox from "@/pages/Inbox";
import AuthCallback from "@/pages/AuthCallback";
import ForcePasswordChange from "@/pages/ForcePasswordChange";

function ProtectedRoute({ children, adminOnly }) {
  const { user, ready, isAdmin } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user, ready } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={ready && user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
      <Route path="/shipments" element={<ProtectedRoute><Shipments /></ProtectedRoute>} />
      <Route path="/new-shipments" element={<ProtectedRoute><NewShipments /></ProtectedRoute>} />
      <Route path="/reverse-pickup" element={<ProtectedRoute><ReversePickup /></ProtectedRoute>} />
      <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
      <Route path="/shipments/:id" element={<ProtectedRoute><ShipmentDetail /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute adminOnly><Settings /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute adminOnly><Users /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App font-body">
      <AuthProvider>
        <StatusProvider>
          <InboxProvider>
            <ReshipProvider>
              <RvpProvider>
                <BrowserRouter>
                  <AppRoutes />
                  <Toaster position="top-right" richColors />
                </BrowserRouter>
              </RvpProvider>
            </ReshipProvider>
          </InboxProvider>
        </StatusProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
